/**
 * Hydrawise Integration Store
 *
 * Zustand store for Hunter Hydrawise irrigation controller state.
 * Manages controllers, zones, settings, and Socket.IO real-time updates.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { api } from '@/services/api/client';

// ============================================================================
// Types
// ============================================================================

export interface HydrawiseController {
  controllerId: string;
  name: string;
  status: 'online' | 'offline';
  lastContact: string;
  serialNumber?: string;
}

export interface HydrawiseZone {
  relayId: string;
  relayNumber: number;
  name: string;
  status: 'running' | 'idle' | 'suspended';
  timeRemaining?: number;
  nextRun?: string;
  nextRunDescription: string;
}

interface HydrawiseState {
  // Settings
  hasApiKey: boolean;
  isActive: boolean;

  // Data
  controllers: HydrawiseController[];
  zones: Record<string, HydrawiseZone[]>; // controllerId -> zones

  // UI state
  isLoading: boolean;
  isControlling: boolean;
  error: string | null;

  // Actions
  fetchSettings: () => Promise<void>;
  saveApiKey: (apiKey: string) => Promise<void>;
  removeApiKey: () => Promise<void>;
  fetchControllers: () => Promise<void>;
  fetchZones: (controllerId: string) => Promise<void>;
  runZone: (relayId: string, durationSeconds: number) => Promise<void>;
  stopZone: (relayId: string) => Promise<void>;
  suspendZone: (relayId: string, durationSeconds: number) => Promise<void>;
  runAllZones: (controllerId: string, durationSeconds: number) => Promise<void>;
  stopAllZones: (controllerId: string) => Promise<void>;
  startPolling: (controllerId: string) => Promise<void>;
  stopPolling: (pollingId: string) => Promise<void>;
  handleZoneStatusUpdate: (data: { controllerId: string; zones: HydrawiseZone[] }) => void;
  clearError: () => void;
}

const API_BASE = '/api/v1/integrations/hydrawise';

// ============================================================================
// Store
// ============================================================================

export const useHydrawiseStore = create<HydrawiseState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      hasApiKey: false,
      isActive: false,
      controllers: [],
      zones: {},
      isLoading: false,
      isControlling: false,
      error: null,

      fetchSettings: async () => {
        try {
          const response = await api.get<{ hasApiKey: boolean; isActive: boolean; config: Record<string, any> }>(`${API_BASE}/settings`);
          set((state) => {
            state.hasApiKey = response.data?.hasApiKey ?? false;
            state.isActive = response.data?.isActive ?? false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to fetch settings';
          });
        }
      },

      saveApiKey: async (apiKey: string) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const response = await api.put<{ hasApiKey: boolean; controllers: HydrawiseController[] }, { apiKey: string }>(
            `${API_BASE}/settings`,
            { apiKey }
          );
          set((state) => {
            state.hasApiKey = true;
            state.isActive = true;
            state.controllers = response.data?.controllers ?? [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to save API key';
            state.isLoading = false;
          });
          throw error;
        }
      },

      removeApiKey: async () => {
        try {
          await api.delete(`${API_BASE}/settings`);
          set((state) => {
            state.hasApiKey = false;
            state.isActive = false;
            state.controllers = [];
            state.zones = {};
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to remove API key';
          });
        }
      },

      fetchControllers: async () => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const response = await api.get<HydrawiseController[]>(`${API_BASE}/controllers`);
          set((state) => {
            state.controllers = response.data ?? [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to fetch controllers';
            state.isLoading = false;
          });
        }
      },

      fetchZones: async (controllerId: string) => {
        set((state) => { state.isLoading = true; state.error = null; });
        try {
          const response = await api.get<HydrawiseZone[]>(`${API_BASE}/zones/${controllerId}`);
          set((state) => {
            state.zones[controllerId] = response.data ?? [];
            state.isLoading = false;
          });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to fetch zones';
            state.isLoading = false;
          });
        }
      },

      runZone: async (relayId: string, durationSeconds: number) => {
        set((state) => { state.isControlling = true; state.error = null; });
        try {
          await api.post(`${API_BASE}/zones/${relayId}/run`, { durationSeconds });
          set((state) => { state.isControlling = false; });
          // Refresh zone status for all controllers
          for (const controller of get().controllers) {
            get().fetchZones(controller.controllerId);
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to run zone';
            state.isControlling = false;
          });
        }
      },

      stopZone: async (relayId: string) => {
        set((state) => { state.isControlling = true; state.error = null; });
        try {
          await api.post(`${API_BASE}/zones/${relayId}/stop`, {});
          set((state) => { state.isControlling = false; });
          for (const controller of get().controllers) {
            get().fetchZones(controller.controllerId);
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to stop zone';
            state.isControlling = false;
          });
        }
      },

      suspendZone: async (relayId: string, durationSeconds: number) => {
        set((state) => { state.isControlling = true; state.error = null; });
        try {
          await api.post(`${API_BASE}/zones/${relayId}/suspend`, { durationSeconds });
          set((state) => { state.isControlling = false; });
          for (const controller of get().controllers) {
            get().fetchZones(controller.controllerId);
          }
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to suspend zone';
            state.isControlling = false;
          });
        }
      },

      runAllZones: async (controllerId: string, durationSeconds: number) => {
        set((state) => { state.isControlling = true; state.error = null; });
        try {
          await api.post(`${API_BASE}/zones/runall`, { controllerId, durationSeconds });
          set((state) => { state.isControlling = false; });
          get().fetchZones(controllerId);
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to run all zones';
            state.isControlling = false;
          });
        }
      },

      stopAllZones: async (controllerId: string) => {
        set((state) => { state.isControlling = true; state.error = null; });
        try {
          await api.post(`${API_BASE}/zones/stopall`, { controllerId });
          set((state) => { state.isControlling = false; });
          get().fetchZones(controllerId);
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to stop all zones';
            state.isControlling = false;
          });
        }
      },

      startPolling: async (controllerId: string) => {
        try {
          await api.post(`${API_BASE}/polling/start`, { controllerId, intervalMs: 60000 });
        } catch (error) {
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to start polling';
          });
        }
      },

      stopPolling: async (pollingId: string) => {
        try {
          await api.delete(`${API_BASE}/polling/${pollingId}`);
        } catch {
          // Ignore polling stop errors
        }
      },

      handleZoneStatusUpdate: (data: { controllerId: string; zones: HydrawiseZone[] }) => {
        set((state) => {
          state.zones[data.controllerId] = data.zones;
        });
      },

      clearError: () => {
        set((state) => { state.error = null; });
      },
    }))
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const useHydrawiseSettings = () => {
  const { hasApiKey, isActive } = useHydrawiseStore();
  return { hasApiKey, isActive };
};

export const useHydrawiseControllers = () => {
  const { controllers, isLoading, error } = useHydrawiseStore();
  return { controllers, isLoading, error };
};

export const useHydrawiseZones = (controllerId: string) => {
  const zones = useHydrawiseStore((s) => s.zones[controllerId] ?? []);
  const isLoading = useHydrawiseStore((s) => s.isLoading);
  return { zones, isLoading };
};
